import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageIcon from '@mui/icons-material/Image';

import { useStyles } from './styles';
import { CustomSpotsEditorController } from './useCustomSpotsEditor';

type EditorContentProps = {
  classes: ReturnType<typeof useStyles>;
  controller: CustomSpotsEditorController;
};

const UploadPrompt: React.FC<{
  classes: ReturnType<typeof useStyles>;
  title: string;
  subtitle: string;
  uploadLabel: string;
  disabled: boolean;
  onUploadClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ classes, title, subtitle, uploadLabel, disabled, onUploadClick }) => (
  <>
    <UploadFileIcon sx={classes.uploadIcon} />
    <Typography sx={classes.dropZoneTitle}>{title}</Typography>
    <Typography sx={classes.dropZoneSubtitle}>{subtitle}</Typography>
    <Button
      variant="text"
      onClick={onUploadClick}
      disabled={disabled}
      sx={classes.linkButton}
    >
      {uploadLabel}
    </Button>
  </>
);

const VideoEditor: React.FC<EditorContentProps> = ({ classes, controller }) => {
  const {
    t,
    isDragging,
    setIsDragging,
    isPreviewImageDragging,
    setIsPreviewImageDragging,
    isBusy,
    selectedVideoFile,
    selectedVideoLocalUrl,
    videoPreviewLocalUrl,
    videoFileInputRef,
    previewImageFileInputRef,
    handleVideoFileSelect,
    handleClearSelectedVideo,
    handlePreviewImageFileSelect,
  } = controller;

  return (
    <Box sx={classes.videoMediaGrid}>
      <Box
        sx={[
          classes.videoMediaCard,
          isDragging && classes.dropZoneActive,
          selectedVideoFile && classes.videoMediaCardSelected,
        ]}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(false);
          handleVideoFileSelect(event.dataTransfer.files);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(true);
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(false);
        }}
        onClick={
          !selectedVideoFile
            ? () => videoFileInputRef.current?.click()
            : undefined
        }
      >
        {selectedVideoFile ? (
          <Box sx={classes.videoPreviewCardContent}>
            <Box
              component="video"
              src={selectedVideoLocalUrl || undefined}
              controls
              preload="metadata"
              sx={classes.videoPreviewPlayer}
              onClick={(event: React.MouseEvent<HTMLVideoElement>) => {
                event.stopPropagation();
              }}
            />
            <Box sx={classes.mediaCardFooter}>
              <Typography sx={classes.selectedFileName}>
                {selectedVideoFile.name}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClearSelectedVideo();
                }}
                disabled={isBusy}
                sx={classes.linkButton}
              >
                {t('customSpotsEditor.changeVideo')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={classes.mediaUploadPromptWrapper}>
            <Typography sx={classes.videoMediaLabel}>
              {t('customSpotsEditor.videoFileLabel')}
            </Typography>
            <UploadPrompt
              classes={classes}
              title={t('customSpotsEditor.dropVideoTitle')}
              subtitle={t('customSpotsEditor.dropSubtitle')}
              uploadLabel={t('customSpotsEditor.uploadLink')}
              disabled={isBusy}
              onUploadClick={(event) => {
                event.stopPropagation();
                videoFileInputRef.current?.click();
              }}
            />
          </Box>
        )}
        <input
          ref={videoFileInputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
          hidden
          onChange={(event) => handleVideoFileSelect(event.target.files)}
        />
      </Box>

      <Box sx={classes.previewImageSection}>
        {videoPreviewLocalUrl ? (
          <Box
            sx={classes.previewImageThumbWrapper}
            onClick={() => previewImageFileInputRef.current?.click()}
          >
            <Box
              component="img"
              src={videoPreviewLocalUrl}
              alt="preview"
              sx={classes.previewImageThumb}
            />
            <Box
              className="preview-image-overlay"
              sx={classes.previewImageOverlay}
            >
              <ImageIcon sx={classes.previewImageOverlayIcon} />
              <Typography sx={classes.previewImageOverlayText}>
                {t('customSpotsEditor.changeImage')}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={[
              classes.previewImageDropZone,
              isPreviewImageDragging && classes.dropZoneActive,
            ]}
            onDrop={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsPreviewImageDragging(false);
              handlePreviewImageFileSelect(event.dataTransfer.files);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsPreviewImageDragging(true);
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsPreviewImageDragging(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsPreviewImageDragging(false);
            }}
            onClick={() => previewImageFileInputRef.current?.click()}
          >
            <Typography sx={classes.videoMediaLabel}>
              {t('customSpotsEditor.previewImageLabel')}
              <Box component="span" sx={classes.requiredMark}>
                {' *'}
              </Box>
            </Typography>
            <UploadFileIcon sx={classes.previewImageUploadIcon} />
            <Typography sx={classes.previewImageDropText}>
              {t('customSpotsEditor.dropPreviewImage')}
            </Typography>
            <Typography sx={classes.previewImageDropHint}>
              PNG, JPG, JPEG
            </Typography>
          </Box>
        )}
        <input
          ref={previewImageFileInputRef}
          type="file"
          accept="image/png,image/jpeg,.png,.jpg,.jpeg"
          hidden
          onChange={(event) => handlePreviewImageFileSelect(event.target.files)}
        />
      </Box>
    </Box>
  );
};

const ImageEditor: React.FC<EditorContentProps> = ({ classes, controller }) => {
  const {
    t,
    isDragging,
    setIsDragging,
    selectedFile,
    displayedPreviewUrl,
    hasPreview,
    targetAspect,
    isUploading,
    fileInputRef,
    handleImageFiles,
    setSelectedImageSize,
  } = controller;

  return (
    <Box
      sx={[
        classes.dropZone,
        isDragging && classes.dropZoneActive,
        hasPreview && classes.previewZone,
      ]}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        handleImageFiles(event.dataTransfer.files);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
      }}
      onClick={!selectedFile ? () => fileInputRef.current?.click() : undefined}
    >
      {displayedPreviewUrl ? (
        <Box sx={classes.getPreviewFrame(targetAspect)}>
          <Box
            component="img"
            src={displayedPreviewUrl}
            alt="preview"
            draggable={false}
            sx={classes.previewImage}
            onDragStart={(event: React.DragEvent<HTMLImageElement>) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onLoad={(event: React.SyntheticEvent<HTMLImageElement>) => {
              const img = event.currentTarget;

              if (img.naturalWidth && img.naturalHeight) {
                setSelectedImageSize({
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                });
              }
            }}
          />
        </Box>
      ) : (
        <UploadPrompt
          classes={classes}
          title={t('customSpotsEditor.dropTitle')}
          subtitle={t('customSpotsEditor.dropSubtitle')}
          uploadLabel={t('customSpotsEditor.uploadLink')}
          disabled={isUploading}
          onUploadClick={(event) => {
            event.stopPropagation();
            fileInputRef.current?.click();
          }}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,.png,.jpg,.jpeg"
        hidden
        onChange={(event) => handleImageFiles(event.target.files)}
      />
    </Box>
  );
};

const MetaRow: React.FC<EditorContentProps> = ({ classes, controller }) => {
  const { t, selectedCustomizationUeId, requirementsText, handleCopySpotId } =
    controller;

  return (
    <Box sx={classes.requirementsRow}>
      <Box sx={classes.requirementsLeft}>
        <Typography sx={classes.requirementsText}>
          {t('customSpotsEditor.spotId')}{' '}
          <Box component="span" sx={classes.requirementsStrong}>
            {selectedCustomizationUeId || t('customSpotsEditor.none')}
          </Box>
        </Typography>
        {selectedCustomizationUeId ? (
          <IconButton
            size="small"
            onClick={handleCopySpotId}
            sx={classes.copyButton}
            aria-label={t('common.copy')}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        ) : null}
      </Box>
      {requirementsText ? (
        <Typography sx={classes.requirementsText}>
          {requirementsText}
        </Typography>
      ) : null}
    </Box>
  );
};

const StatusRows: React.FC<EditorContentProps> = ({ classes, controller }) => {
  const {
    t,
    error,
    isUploading,
    uploadStepMessage,
    uploadProgress,
    isApplyingToSurface,
  } = controller;

  return (
    <>
      {error ? (
        <Typography sx={classes.errorText} variant="body2">
          {error}
        </Typography>
      ) : null}

      {isUploading ? (
        <Box sx={classes.progressWrapper}>
          <Box sx={classes.statusRow}>
            <CircularProgress size={18} />
            <Typography sx={classes.statusText}>
              {uploadStepMessage || t('customSpotsEditor.uploading')}
            </Typography>
            <Typography sx={classes.progressPercent}>
              {uploadProgress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={classes.linearProgress}
          />
        </Box>
      ) : null}

      {isApplyingToSurface ? (
        <Box sx={classes.statusRow}>
          <CircularProgress size={18} />
          <Typography sx={classes.statusText}>
            {t('customSpotsEditor.applying')}
          </Typography>
        </Box>
      ) : null}
    </>
  );
};

export const EditorContent: React.FC<EditorContentProps> = ({
  classes,
  controller,
}) => (
  <>
    {controller.isVideoSpot ? (
      <VideoEditor classes={classes} controller={controller} />
    ) : (
      <ImageEditor classes={classes} controller={controller} />
    )}
    <MetaRow classes={classes} controller={controller} />
    <StatusRows classes={classes} controller={controller} />
  </>
);
